import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { ArchiveDialog } from "../../generic/components/archive-dialog";
import { ConditionalWrapper } from "../../generic/components/conditional-wrapper";
import { useArchiveMoment } from "../hooks/use-archive-moment";

import type { UUID } from "../../../utils/uuid";
import type { FC, ReactElement } from "react";

interface CardArchiveButtonProps {
  id: UUID;
  isDeleted: boolean;
  isArchived: boolean;
  isActive: boolean;
  unsavedChanges: boolean;
  status: "draft" | "paused" | "finished" | "active" | "archived" | null;
  onClicked?: (archived: boolean) => void;
}

export const CardArchiveButton = ({
  id,
  isActive,
  isArchived,
  isDeleted,
  unsavedChanges,
  onClicked,
  status,
}: CardArchiveButtonProps): ReactElement => {
  const dialog = useDialog();

  const archiveTooltip = isActive
    ? "Archive is unavailable for active Communications. Please pause the Communication first."
    : unsavedChanges
      ? "Save your changes to archive this Communication."
      : "Archive Communication";

  return (
    <>
      {isDeleted ? (
        <Tooltip title="Unarchive" placement="top" arrow>
          <IconButton data-analytics-id="moment-card-unarchive" onClick={dialog.handleOpen}>
            <RestoreFromTrash data-testid="RestoreFromTrashIcon" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip
          title={<Box sx={{ textAlign: "center" }}>{archiveTooltip}</Box>}
          placement="top"
          arrow
        >
          <ConditionalWrapper
            condition={isActive || unsavedChanges}
            // oxlint-disable-next-line react/no-unstable-nested-components -- ConditionalWrapper calls wrapper() as a plain function; it is never mounted as a component
            wrapper={(children) => <span>{children}</span>}
          >
            <IconButton
              data-analytics-id="moment-card-archive"
              onClick={dialog.handleOpen}
              disabled={isActive || unsavedChanges}
            >
              <DeleteOutlined data-testid="DeleteOutlineIcon" />
            </IconButton>
          </ConditionalWrapper>
        </Tooltip>
      )}
      <CardArchiveDialog
        id={id}
        status={status}
        open={dialog.open}
        setOpen={dialog.setOpen}
        moment_archived={isArchived}
        onClicked={onClicked}
      />
    </>
  );
};

const CardArchiveDialog: FC<{
  id: UUID;
  open: boolean;
  setOpen: (open: boolean) => void;
  moment_archived: boolean;
  onClicked?: (archived: boolean) => void;
  status: "draft" | "paused" | "finished" | "active" | "archived" | null;
}> = ({ open, setOpen, moment_archived, id, onClicked, status }) => {
  const { archive, archiving } = useArchiveMoment(id, status);

  return (
    <ArchiveDialog
      open={open}
      onClose={() => setOpen(false)}
      kind="Communication"
      unarchive={moment_archived}
      archiving={archiving}
      analyticsId="moment-card"
      onConfirm={() => {
        setOpen(false);
        void (async () => {
          if (await archive(!moment_archived)) onClicked?.(!moment_archived);
        })().catch(console.error);
      }}
    />
  );
};
